import { useState } from 'react';
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, TextField } from '@mui/material';
import { REFERRAL_STATUS_LABELS, REFERRAL_STATUS_REASON_MAX, type ReferralStatus, type ReviewableReferralStatus } from '@hv/shared';
import { useChangeReferralStatus } from '../api/hooks';
import { useNotify } from '../components/Notify';

export const REFERRAL_STATUS_COLOR: Record<ReferralStatus, 'default' | 'info' | 'warning' | 'primary' | 'success'> = {
  draft: 'default',
  submitted: 'info',
  needs_clarification: 'warning',
  under_review: 'primary',
  reviewed: 'success',
  archived: 'default',
};

export function ReferralStatusChip({ status }: { status: ReferralStatus }) {
  return <Chip size="small" color={REFERRAL_STATUS_COLOR[status]} variant={status === 'archived' ? 'outlined' : 'filled'} label={REFERRAL_STATUS_LABELS[status]} />;
}

interface Target {
  id: string;
  status: ReferralStatus;
  allowedTransitions: ReferralStatus[];
  referenceNumber: string;
}

export function ReferralStatusControl({ referral, canChange }: { referral: Target; canChange: boolean }) {
  const { notify, notifyError } = useNotify();
  const change = useChangeReferralStatus();
  const [next, setNext] = useState<ReviewableReferralStatus | null>(null);
  const [reason, setReason] = useState('');
  const clarifying = next === 'needs_clarification';
  const label = `Status of ${referral.referenceNumber}`;

  if (!canChange || !referral.allowedTransitions.length) return <ReferralStatusChip status={referral.status} />;

  const close = () => {
    setNext(null);
    setReason('');
  };
  const confirm = () => {
    if (!next) return;
    change.mutate(
      { id: referral.id, fromStatus: referral.status, status: next, reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          notify('success', clarifying ? `Clarification requested for ${referral.referenceNumber}` : `${referral.referenceNumber} moved to ${REFERRAL_STATUS_LABELS[next]}`);
          close();
        },
        onError: (err) => {
          notifyError(err, 'Changing the referral status failed');
          close();
        },
      },
    );
  };

  return (
    <>
      <TextField
        select
        size="small"
        value={referral.status}
        onChange={(e) => setNext(e.target.value as ReviewableReferralStatus)}
        slotProps={{ htmlInput: { 'aria-label': label } }}
        sx={{ minWidth: 190 }}
      >
        <MenuItem value={referral.status}>{REFERRAL_STATUS_LABELS[referral.status]}</MenuItem>
        {referral.allowedTransitions.map((s) => (
          <MenuItem key={s} value={s}>
            {REFERRAL_STATUS_LABELS[s]}
          </MenuItem>
        ))}
      </TextField>
      <Dialog open={!!next} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>{clarifying ? 'Ask the trustee for clarification' : `Move ${referral.referenceNumber} to ${next ? REFERRAL_STATUS_LABELS[next] : ''}?`}</DialogTitle>
        <DialogContent>
          <DialogContentText mb={2}>
            {clarifying
              ? 'Explain what the trustee needs to clarify. They get an email asking them to sign in; the email does not include your explanation. It is kept with the referral and its status history.'
              : next === 'archived'
                ? 'Archiving keeps the recommendation, the household and its scoring. The change is recorded in the status history and Admissions is emailed.'
                : 'The change is recorded in the status history and Admissions is emailed.'}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={clarifying ? 4 : 2}
            label={clarifying ? 'What needs clarifying' : 'Note for the status history (optional)'}
            required={clarifying}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            slotProps={{ htmlInput: { maxLength: REFERRAL_STATUS_REASON_MAX } }}
            helperText={`${reason.length}/${REFERRAL_STATUS_REASON_MAX}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" onClick={confirm} disabled={change.isPending || (clarifying && !reason.trim())}>
            {clarifying ? 'Send request' : 'Change status'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
